import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  ConsultationSyncPushInput,
  QueryPullConsultationArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Consultation } from "../collections/consultation";
import type { RxDBCollections } from "../provider";
import {
  SyncConsultationPullDocument,
  SyncConsultationPushDocument,
  type WholeSyncableConsultationFragment,
} from "./__generated__/consultation.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateConsultation = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<Consultation>;
}) => {
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");
  const patientId = matchPat?.params.patientId ?? query.patientUuid;
  const push = useMemo(
    () => ({
      mutation: SyncConsultationPushDocument,
      mapRxToGql: mapRxConsultationToGqlConsultation,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableConsultationFragment>
  >(() => {
    return patientId
      ? {
          query: SyncConsultationPullDocument,
          reSyncInterval: { seconds: 30 },
          ...createPersistedCheckpointPullBuilders<
            QueryPullConsultationArgs,
            WholeSyncableConsultationFragment
          >({
            buildInputVariables: (checkpoint, batchSize) => ({
              input: { limit: batchSize, filters: { patientId }, checkpoint },
            }),
            buildPullCheckpoint: (doc) => ({
              id: doc.id,
              updatedAt: doc.meta.updatedAt,
            }),
            entityId: patientId,
            db,
            collectionName: collection?.name,
          }),
        }
      : undefined;
  }, [patientId, db, collection]);

  return useGqlReplication<
    Consultation,
    WholeSyncableConsultationFragment,
    ConsultationSyncPushInput
  >({
    db,
    collection,
    push,
    pull,
    mapGqlToRx: mapGqlConsultationToRxConsultation,
    batchSize: 100,
    expireData: true,
    metadataLocalStorageKeySuffix: `consultation-${patientId}`,
  });
};

export const mapGqlConsultationToRxConsultation = (
  consultation: WholeSyncableConsultationFragment,
) => ({
  id: consultation.id,
  patientId: consultation.patientId,
  meta: {
    createdAt: consultation.meta.createdAt,
    createdByUserId: consultation.meta.createdByUserId,
    updatedAt: consultation.meta.updatedAt,
    updatedByUserId: consultation.meta.updatedByUserId,
    deleted: consultation.meta.deleted,
  },
  diseaseIds: consultation.diseaseIds ? [...consultation.diseaseIds] : [],
  freeTextDiagnoses: consultation.freeTextDiagnoses.map(
    (freeTextDiagnosis) => ({
      id: freeTextDiagnosis.id,
      name: freeTextDiagnosis.name,
      category: freeTextDiagnosis.category,
    }),
  ),
  plan: consultation.plan,
  presentingComplaint: consultation.presentingComplaint,
  symptomIds: [...consultation.symptomIds],
  problem: consultation.problem,
  facilityId: consultation.facilityId,
  organisationId: consultation.organisationId,
  isImmunisation: consultation.isImmunisation ?? null,
  isPerformedAtOtherClinic: consultation.isPerformedAtOtherClinic ?? null,
  backdatedTo: consultation.backdatedTo ?? null,
  _deleted: consultation.meta.deleted,
});

const mapRxConsultationToGqlConsultation = (
  consultation: WithDeleted<Consultation>,
) => ({
  id: consultation.id,
  patientId: consultation.patientId,
  meta: {
    createdAt: consultation.meta.createdAt,
    createdByUserId: consultation.meta.createdByUserId,
    updatedAt: consultation.meta.updatedAt,
    updatedByUserId: consultation.meta.updatedByUserId,
    deleted: consultation._deleted,
  },
  diseaseIds: consultation.diseaseIds,
  freeTextDiagnoses: consultation.freeTextDiagnoses.map(
    (freeTextDiagnosis) => ({
      id: freeTextDiagnosis.id,
      name: freeTextDiagnosis.name,
      category: freeTextDiagnosis.category,
    }),
  ),
  plan: consultation.plan,
  presentingComplaint: consultation.presentingComplaint,
  symptomIds: [...consultation.symptomIds],
  problem: consultation.problem,
  facilityId: consultation.facilityId,
  organisationId: consultation.organisationId,
  isImmunisation: consultation.isImmunisation,
  isPerformedAtOtherClinic: consultation.isPerformedAtOtherClinic,
  backdatedTo: consultation.backdatedTo,
});

gql`
  fragment WholeSyncableConsultation on SyncableConsultation {
    id
    patientId
    organisationId
    facilityId
    meta {
        deleted
        updatedAt
        createdAt
        updatedByUserId
        createdByUserId
    }
    freeTextDiagnoses {
      id
      name
      category
    }
    diseaseIds
    plan
    problem
    presentingComplaint
    symptomIds
    isImmunisation
    isPerformedAtOtherClinic
    backdatedTo
}
`;

gql`
  mutation SyncConsultationPush($input:PushConsultationInput!) {
    pushConsultation(input: $input) {
      rows {
        ...WholeSyncableConsultation
      }
    }
  }
`;

gql`
  query SyncConsultationPull($input:PullConsultationInput!) {
    pullConsultation(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableConsultation
      }
    }
  }
`;
